import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CreditCard, Search } from "lucide-react";

const money = (v) => `$${(v || 0).toFixed(2)}`;
const TIER_STYLE = { pro: "bg-[#24468C] text-white", lite: "bg-[#E8A93C] text-[#1a3060]" };
const STATUS_STYLE = {
  active: "bg-green-100 text-green-800", trialing: "bg-[#E8A93C]/15 text-[#8a6410]",
  canceled: "bg-red-100 text-red-800", past_due: "bg-amber-100 text-amber-800",
};

export default function AdminSubscriptions() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [tier, setTier] = useState("all");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== "all") params.status = status;
      if (tier !== "all") params.tier = tier;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/admin/subscriptions", { params });
      setRows(data);
    } catch (e) { logger.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  }, [status, tier, q]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, tier]);

  const mrr = rows.reduce((a, r) => a + (r.mrr_usd || 0), 0);

  return (
    <div className="space-y-6" data-testid="admin-subscriptions">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2"><CreditCard className="h-7 w-7 text-[#24468C]" strokeWidth={1.75} /> Subscriptions</h1>
        <p className="text-muted-foreground text-sm">All plans across the platform. Filtered MRR: <strong className="text-zinc-900">{money(mrr)}</strong></p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search email" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="subs-search-input" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40" data-testid="subs-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="trialing">Trialing</SelectItem>
            <SelectItem value="canceled">Canceled</SelectItem>
            <SelectItem value="past_due">Past due</SelectItem>
          </SelectContent>
        </Select>
        <Select value={tier} onValueChange={setTier}>
          <SelectTrigger className="w-32" data-testid="subs-tier-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tiers</SelectItem>
            <SelectItem value="lite">Lite</SelectItem>
            <SelectItem value="pro">Pro</SelectItem>
            <SelectItem value="trial">Trial</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={load} data-testid="subs-apply-btn">Apply</Button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="py-20 text-center"><Loader2 className="h-8 w-8 mx-auto animate-spin text-[#24468C]" /></div>
        ) : rows.length === 0 ? (
          <div className="py-20 text-center text-muted-foreground" data-testid="subs-empty">No subscriptions match these filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="subs-table">
              <thead>
                <tr className="border-b border-zinc-200 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-semibold">Email</th>
                  <th className="px-4 py-3 font-semibold">Tier</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Plan</th>
                  <th className="px-4 py-3 font-semibold text-right">MRR</th>
                  <th className="px-4 py-3 font-semibold">Renews</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.user_id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`sub-row-${s.user_id}`}>
                    <td className="px-4 py-3 font-medium">{s.email}</td>
                    <td className="px-4 py-3"><Badge className={`${TIER_STYLE[s.tier] || "bg-zinc-100 text-zinc-600"} capitalize`}>{s.tier || "—"}</Badge></td>
                    <td className="px-4 py-3"><Badge className={STATUS_STYLE[s.status] || "bg-zinc-100 text-zinc-600"}>{s.status || "—"}</Badge></td>
                    <td className="px-4 py-3 text-muted-foreground">{s.lookup_key || "—"}</td>
                    <td className="px-4 py-3 text-right font-medium">{s.mrr_usd ? money(s.mrr_usd) : "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{s.current_period_end ? new Date(s.current_period_end).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
